import React from 'react';
import AudioPlayer from '../components/AudioPlayer';
import VideoPlayer from '../components/VideoPlayer';
import AdBanner from '../components/AdBanner';
import { Radio, Tv, Headphones, Globe } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { usePageSEO } from '../components/SEO';

const Home = () => {
  const { t, language } = useTranslation();
  usePageSEO({
    it: {
      title: 'Rete Italia Radio — Radio Italiana in Australia | Streaming Live 24/7',
      description: 'Rete Italia è la radio italiana in Australia: musica italiana, programmi culturali, notizie e podcast 24 ore su 24. Streaming live audio e video da Melbourne e Sydney.',
    },
    en: {
      title: 'Rete Italia Radio — Italian Radio in Australia | Live Streaming 24/7',
      description: 'Rete Italia is the Italian radio station in Australia: Italian music, cultural programs, news and podcasts 24/7. Live audio and video streaming from Melbourne and Sydney.',
    },
    basePath: '/',
  });
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4">
        <div className="container mx-auto">
          {/* Advertisement Banner */}
          <AdBanner className="mb-8" />

          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-6xl font-bold mb-4">
              <span className="bg-gradient-to-r from-[#009246] via-[#CE2B37] to-[#00247D] bg-clip-text text-transparent">
                {t('home.welcome')}
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto">
              {t('home.subtitle')}
            </p>
          </div>

          {/* Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-16">
            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border-t-4 border-[#009246]">
              <Radio className="w-10 h-10 text-[#009246] mb-3" />
              <h3 className="font-bold text-lg mb-2">{t('home.liveRadio')}</h3>
              <p className="text-gray-600 text-sm">{t('home.liveRadioDesc')}</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border-t-4 border-[#CE2B37]">
              <Tv className="w-10 h-10 text-[#CE2B37] mb-3" />
              <h3 className="font-bold text-lg mb-2">{t('home.liveVideo')}</h3>
              <p className="text-gray-600 text-sm">{t('home.liveVideoDesc')}</p>
            </div>
            <a href="/podcast" className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-t-4 border-[#00247D] hover:scale-105 block">
              <Headphones className="w-10 h-10 text-[#00247D] mb-3" />
              <h3 className="font-bold text-lg mb-2">{t('home.podcastTitle')}</h3>
              <p className="text-gray-600 text-sm">{t('home.podcastDesc')}</p>
            </a>
            <a href="#ascoltaci-su" className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-t-4 border-gray-800 hover:scale-105 block">
              <Globe className="w-10 h-10 text-gray-800 mb-3" />
              <h3 className="font-bold text-lg mb-2">{t('home.everywhere')}</h3>
              <p className="text-gray-600 text-sm">{t('home.everywhereDesc')}</p>
            </a>
          </div>
        </div>
      </section>

      {/* Players Section */}
      <section className="pb-16 px-4">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Audio Player */}
            <div>
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <Radio className="w-6 h-6 text-[#009246]" />
                {t('home.audioStreaming')}
              </h2>
              <AudioPlayer />
            </div>

            {/* Video Player */}
            <div>
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <Tv className="w-6 h-6 text-[#00247D]" />
                {t('home.videoStreaming')}
              </h2>
              <VideoPlayer />
            </div>
          </div>

          {/* Frequencies Box */}
          <div id="ascoltaci-su" className="mt-8 bg-gradient-to-r from-[#00247D] to-[#0047AB] rounded-2xl p-6 text-white shadow-xl">
            <div className="text-center">
              <h3 className="text-xl font-bold mb-4 flex items-center justify-center gap-2">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.636 18.364a9 9 0 010-12.728m12.728 0a9 9 0 010 12.728m-9.9-2.829a5 5 0 010-7.07m7.072 0a5 5 0 010 7.07M13 12a1 1 0 11-2 0 1 1 0 012 0z" />
                </svg>
                {language === 'it' ? 'Ascoltaci Su' : 'Listen To Us On'}
              </h3>
              <div className="space-y-4">
                <div className="flex flex-wrap justify-center gap-3 text-base">
                  <span className="bg-white/20 px-3 py-1 rounded-lg font-semibold">Sydney <span className="text-yellow-300">DAB+</span></span>
                  <span className="bg-white/20 px-3 py-1 rounded-lg font-semibold">Melbourne <span className="text-yellow-300">DAB+</span></span>
                  <span className="bg-white/20 px-3 py-1 rounded-lg font-semibold">Brisbane <span className="text-yellow-300">DAB+</span></span>
                  <span className="bg-white/20 px-3 py-1 rounded-lg font-semibold">Perth (Niche Radio) <span className="text-yellow-300">DAB+</span></span>
                </div>
                <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-sm opacity-90">
                  <span>Colac - 87.8 FM</span>
                  <span>•</span>
                  <span>Shepparton - 87.6 FM</span>
                  <span>•</span>
                  <span>Wangaratta - 87.8 FM</span>
                  <span>•</span>
                  <span>Wodonga - 87.6 FM</span>
                  <span>•</span>
                  <span>Albury - 87.6 FM</span>
                  <span>•</span>
                  <span>Swan Hill - 87.6 FM</span>
                </div>
                
                {/* App Download Links */}
                <div className="pt-4 border-t border-white/20">
                  <p className="text-sm mb-3 opacity-80">{language === 'it' ? 'Scarica la nostra App' : 'Download our App'}</p>
                  <div className="flex flex-wrap justify-center gap-4">
                    <a 
                      href="https://play.google.com/store/apps/details?id=com.xdevel.reteitaliaaustralia2&hl=en_AU" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-black hover:bg-gray-900 px-4 py-2 rounded-lg transition-all duration-300 hover:scale-105"
                    >
                      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 010 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/>
                      </svg>
                      <span className="text-sm font-semibold">Google Play</span>
                    </a>
                    <a 
                      href="https://apps.apple.com/au/app/rete-italia/id1445988538" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-black hover:bg-gray-900 px-4 py-2 rounded-lg transition-all duration-300 hover:scale-105"
                    >
                      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                      </svg>
                      <span className="text-sm font-semibold">App Store</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <div className="bg-gradient-to-r from-[#009246] to-[#00b359] rounded-3xl p-12 text-center text-white shadow-2xl">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {t('home.ctaTitle')}
            </h2>
            <p className="text-xl mb-8 opacity-90">
              {t('home.ctaSubtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contatti"
                className="inline-block bg-white text-[#009246] px-8 py-4 rounded-xl font-bold hover:bg-gray-100 transition-all duration-300 hover:scale-105 shadow-lg"
              >
                {t('home.contactUs')}
              </a>
              <a
                href="/podcast"
                className="inline-block bg-[#00247D] text-white px-8 py-4 rounded-xl font-bold hover:bg-[#001a4d] transition-all duration-300 hover:scale-105 shadow-lg"
              >
                {t('home.explorePodcasts')}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;